import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Doughnut } from 'react-chartjs-2';
import { Eye, MousePointerClick, ShoppingBag, Percent } from 'lucide-react';
import eventAnalyticsApi from 'api/requests/eventAnalytics';
import MiniStatCard from 'modules/dashboard/statistics/components/MiniStatCard';
import { MiniCardSkeleton } from 'modules/dashboard/statistics/components/CardSkeleton';
import dateHelper from 'helpers/date';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import { panelAccent } from 'theme/panelAccent';

/* Exatamente os períodos que GET /events/{id}/analytics aceita (AnalyticsRequest);
   qualquer outro valor volta 422. 30d é o padrão da API. */
const PERIOD_OPTIONS = [
    { value: 'today', label: 'Hoje' },
    { value: 'yesterday', label: 'Ontem' },
    { value: '7d', label: '7 dias' },
    { value: '14d', label: '14 dias' },
    { value: '30d', label: '30 dias' },
    { value: 'all', label: 'Tudo' },
];
const DEFAULT_PERIOD = '30d';

const PAYMENT_METHOD_COLORS = ['#2BC866', '#690ce4', '#4F86F7'];
const PAYMENT_METHOD_LABELS = {
    pix: 'Pix',
    credit_card: 'Cartão',
    free: 'Gratuito',
};

export default function EventAccessCard({ eventId }) {
    const [period, setPeriod] = useState(DEFAULT_PERIOD);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);

    useEffect(() => {
        if (!eventId) {
            return;
        }

        let isMounted = true;
        setLoading(true);
        setErrored(false);

        eventAnalyticsApi.getAnalytics(eventId, period)
            .then(({ data }) => { if (isMounted) setData(data); })
            .catch(() => { if (isMounted) setErrored(true); })
            .finally(() => { if (isMounted) setLoading(false); });

        return () => { isMounted = false; };
    }, [eventId, period]);

    // Só a primeira carga vira esqueleto; trocar de período mantém os números
    // anteriores na tela (esmaecidos) para os cards não piscarem.
    if (loading && !data) {
        return (
            <Wrap>
                <KpiGrid>
                    <MiniCardSkeleton /><MiniCardSkeleton /><MiniCardSkeleton /><MiniCardSkeleton />
                </KpiGrid>
            </Wrap>
        );
    }

    // Seção de bônus, não crítica pro resto da tela: se a primeira carga
    // falhar, some silenciosamente em vez de mostrar um erro. Depois que já
    // houve dados, o seletor fica para a pessoa poder tentar outro período.
    if (!data) {
        return null;
    }

    const periodSelector = (
        <Toolbar>
            <PeriodGroup role='group' aria-label='Período dos acessos'>
                {PERIOD_OPTIONS.map(option => (
                    <PeriodPill
                        key={option.value}
                        type='button'
                        aria-pressed={option.value === period}
                        $active={option.value === period}
                        onClick={() => setPeriod(option.value)}
                    >
                        {option.label}
                    </PeriodPill>
                ))}
            </PeriodGroup>

            <RangeCaption aria-live='polite'>
                {errored ? 'Não foi possível carregar este período. Tente de novo.' : describeRange(data.period)}
            </RangeCaption>
        </Toolbar>
    );

    if (errored) {
        return <Wrap>{periodSelector}</Wrap>;
    }

    const { views, checkout_clicks: checkoutClicks, orders_approved: ordersApproved, conversion, payment_methods: paymentMethods } = data;

    const paymentEntries = Object.keys(PAYMENT_METHOD_LABELS)
        .map((method, index) => ({
            method,
            label: PAYMENT_METHOD_LABELS[method],
            color: PAYMENT_METHOD_COLORS[index],
            count: paymentMethods?.[method]?.count || 0,
            percentage: paymentMethods?.[method]?.percentage || 0,
        }));

    const hasPayments = paymentEntries.some(entry => entry.count > 0);

    const donutData = {
        labels: paymentEntries.map(entry => entry.label),
        datasets: [{
            data: paymentEntries.map(entry => entry.count),
            backgroundColor: paymentEntries.map(entry => entry.color),
            borderWidth: 0,
            hoverBorderWidth: 2,
            hoverBorderColor: '#fff',
        }],
    };

    const donutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 65,
        legend: { display: false },
        tooltips: {
            backgroundColor: '#1A1A1A',
            cornerRadius: 8,
            callbacks: {
                label: (tooltipItem, chartData) => {
                    const label = chartData.labels[tooltipItem.index];
                    const value = chartData.datasets[0].data[tooltipItem.index];
                    return ` ${label}: ${value.toLocaleString('pt-BR')}`;
                },
            },
        },
    };

    return (
        <Wrap>
            {periodSelector}

            <Results aria-busy={loading} $loading={loading}>
                <KpiGrid>
                    <MiniStatCard
                        label="Visualizações"
                        value={(views?.total || 0).toLocaleString('pt-BR')}
                        icon={Eye}
                    />
                    <MiniStatCard
                        label="Cliques em comprar"
                        value={(checkoutClicks?.total || 0).toLocaleString('pt-BR')}
                        icon={MousePointerClick}
                    />
                    <MiniStatCard
                        label="Vendas aprovadas"
                        value={(ordersApproved || 0).toLocaleString('pt-BR')}
                        icon={ShoppingBag}
                    />
                    <MiniStatCard
                        label="Taxa de conversão"
                        value={`${((conversion?.view_to_purchase || 0) * 100).toFixed(1)}%`}
                        icon={Percent}
                    />
                </KpiGrid>

                {hasPayments && (
                    <SplitRow>
                        <DoughnutWrap>
                            <Doughnut data={donutData} options={donutOptions} />
                        </DoughnutWrap>
                        <SplitList>
                            {paymentEntries.map(entry => (
                                <SplitItem key={entry.method}>
                                    <Dot color={entry.color} />
                                    {entry.label}
                                    <strong>{entry.count.toLocaleString('pt-BR')}</strong>
                                </SplitItem>
                            ))}
                        </SplitList>
                    </SplitRow>
                )}
            </Results>
        </Wrap>
    );
}

/**
 * O intervalo que a API de fato usou (`period.start_date`/`end_date`), para a
 * pessoa saber o que "7 dias" ou "Tudo" significam nos números ao lado.
 */
function describeRange(range) {
    if (!range?.start_date || !range?.end_date) {
        return '';
    }

    if (range.type === 'today') {
        return `Hoje, ${dateHelper.format(range.end_date)}`;
    }

    if (range.type === 'yesterday') {
        return `Ontem, ${dateHelper.format(range.start_date)}`;
    }

    if (range.type === 'all') {
        return `Desde ${dateHelper.format(range.start_date)}`;
    }

    return `De ${dateHelper.format(range.start_date)} a ${dateHelper.format(range.end_date)}`;
}

const Wrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px 16px;
`;

const PeriodGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 4px;
    border: 1px solid #e8e8ef;
    border-radius: 12px;
    background: ${colors.neutralLight};
`;

const PeriodPill = styled.button`
    min-height: 36px;
    padding: 0 14px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    transition: background-color .15s ease;
    background: ${({ $active }) => ($active ? panelAccent(colors.secondaryPurple) : 'transparent')};
    color: ${({ $active }) => ($active ? '#fff' : colors.nightDark)};

    &:hover {
        background: ${({ $active }) => ($active ? panelAccent(colors.secondaryPurple) : '#f5f4f8')};
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.secondaryPurple)};
        outline-offset: 2px;
    }
`;

// #5E5B76 sobre branco dá ~6:1 (AA para texto pequeno).
const RangeCaption = styled.p`
    margin: 0;
    font-size: 13px;
    color: #5E5B76;
`;

const Results = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    opacity: ${({ $loading }) => ($loading ? 0.5 : 1)};
    transition: opacity .15s ease;
`;

const KpiGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;

    @media screen and (max-width: ${breakpoints.md}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (max-width: ${breakpoints.sm}) {
        grid-template-columns: 1fr;
    }
`;

const SplitRow = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
`;

const DoughnutWrap = styled.div`
    width: 140px;
    height: 140px;
    flex-shrink: 0;
`;

const SplitList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-width: 160px;
`;

const SplitItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: ${colors.nightDark};

    strong {
        margin-left: auto;
    }
`;

const Dot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ color }) => color};
`;
